/**
 * How a file is presented wherever it appears (ADR-22).
 *
 * The chip in a sentence, the card a bullet becomes, a row in the files table and a slide in the
 * viewer are four views of one artefact, so the icon and the size are resolved in one place —
 * a csv is the same green document everywhere, and a second implementation is how they drift.
 */

import {
  IconFileCode,
  IconFileTypeCsv,
  IconFileTypePdf,
  IconFileTypeXls,
  IconJson,
} from '@tabler/icons-react';

/**
 * @desc The format's icon, at whatever size the surface needs: a card draws it at 20, an inline
 * chip at 16, a table row at 16.
 */
export function getFormatIcon(format: string, size = 20) {
  switch (format) {
    case 'csv':
      return <IconFileTypeCsv size={size} color="var(--mantine-color-green-6)" />;
    case 'xlsx':
      return <IconFileTypeXls size={size} color="var(--mantine-color-teal-6)" />;
    case 'json':
      return <IconJson size={size} color="var(--mantine-color-blue-6)" />;
    case 'html':
      return <IconFileCode size={size} color="var(--mantine-color-orange-6)" />;
    case 'pdf':
      return <IconFileTypePdf size={size} color="var(--mantine-color-red-6)" />;
    default:
      return <IconFileCode size={size} color="var(--mantine-color-gray-6)" />;
  }
}

const UNITS = ['B', 'kB', 'MB', 'GB'] as const;

/**
 * @desc A byte count as a person reads it — `48 kB`, `1.2 MB`. Undefined in, undefined out: a
 * file whose producing tool reported no size says nothing rather than showing a zero.
 *
 * Decimal units, matching what the operating system's file listing shows for the same download.
 */
export const formatFileSize = (bytes: number | undefined): string | undefined => {
  if (bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return undefined;

  let value = bytes;
  let unit = 0;
  while (value >= 1000 && unit < UNITS.length - 1) {
    value /= 1000;
    unit += 1;
  }

  return `${unit === 0 || value >= 10 ? Math.round(value) : value.toFixed(1)} ${UNITS[unit]}`;
};
